import * as THREE from 'three';
import React from 'react';
import DropdownProperty from "../../../components/properties/DropdownProperty";
import BoolProperty from '../../../components/properties/BoolProperty';
import NumberProperty from "../../../components/properties/NumberProperty";
import StringProperty from "../../../components/properties/StringProperty";
import { PropertyTypes, EasingTypeToCaptionMap, EasingFunctionTypes, EasingFilter, TimelineEffectorTypes } from '../../../globals';
import store, { StoreActions } from '../../../state/store';
import { assert } from '../../utilities/ErrorHandling';
import { TimelineFragment } from '../TimelineFragment';

export interface ReversibleEffector
{
	reversed : boolean;
}

export interface LoopableEffector
{
	loopCount : number;
}

export interface DurationDeterminingEffector
{
	getInitialDuration : () => number;
}

export default abstract class EffectorBase<T>
{
	fragment : TimelineFragment;
	easing? : EasingFunctionTypes;

	constructor(fragment : TimelineFragment)
	{
		this.fragment = fragment;
	}

	abstract save : () => any;
	abstract load : (savedData : any) => void;
	abstract copyValues : (sourceEffector : T) => void;
	abstract update : (fragmentTime : number) => void;
	abstract resetState : () => void;
	abstract getEditorProperties : () => EffectorPropertyDrawer[];
	abstract onAdded : () => void;

	targetObjects = () => 
	{ 
		return this.fragment.channel.object.getSceneObjects();
	};

	forEachTargetObject = (callback : (targetObject : THREE.Object3D) => void) =>
	{
		const targetObjects = this.fragment.channel.object.getSceneObjects();
		for (const targetObject of targetObjects)
		{
			callback(targetObject);
		}
	};

	isReversibleEffector = (): this is ReversibleEffector =>
	{
		return 'reversed' in this;
	}

	isLoopableEffector = (): this is LoopableEffector =>
	{
		return 'loopCount' in this;
	}
	
	onDelete()
	{
		assert(!this.fragment.timeline.updated);
	}

	reverse()
	{
		if (this.isReversibleEffector()) this.reversed = !this.reversed;
	}

	setEasing(value : EasingFunctionTypes)
	{
		assert(this.easing !== undefined);
		this.easing = value;
	}

	setLoopCount(value : number)
	{	
		if(this.isLoopableEffector()) this.loopCount = value;
	}

	getCommonPropertyDrawers()
	{
		return	[
			...this.fragment.effectorType !== TimelineEffectorTypes.EventInvocation ?
				[new EffectorPropertyDrawer(
					"Name",
					PropertyTypes.String,
					<StringProperty
						value={this.fragment.name}
					/>,
					() => {return this.fragment.name;},
					(v) => {this.fragment.name = v;},
				)] : [],
			new EffectorPropertyDrawer(
				"Start Time",
				PropertyTypes.Number,
				<NumberProperty
					min={0}
					max={ this.fragment.effectorType !== TimelineEffectorTypes.EventInvocation ?
						this.fragment.endTime - 1 / this.fragment.timeline.fps :
						Number.MAX_VALUE}
					step={1 / this.fragment.timeline.fps}
					value={this.fragment.startTime}
					onChangeCallback={(v) =>
					{
						this.fragment.startTime = v;
						if(this.fragment.effectorType === TimelineEffectorTypes.EventInvocation)
						{
							this.fragment.endTime = this.fragment.startTime + 1 / this.fragment.timeline.fps;
						}
						store.dispatch(StoreActions.timeline.updateEditorUI());
					}}
				/>,
				() => {return this.fragment.startTime;},
				(v) =>
				{
					this.fragment.startTime = v;
					store.dispatch(StoreActions.timeline.updateEditorUI());
				 },
			),
			...this.fragment.effectorType !== TimelineEffectorTypes.EventInvocation ?
				[new EffectorPropertyDrawer(
					"End Time",
					PropertyTypes.Number,
					<NumberProperty
						min={this.fragment.startTime + (1 / this.fragment.timeline.fps)}
						step={1 / this.fragment.timeline.fps}
						value={this.fragment.endTime}
						onChangeCallback={(v) =>
						{
							this.fragment.endTime = v;
							store.dispatch(StoreActions.timeline.updateEditorUI());
						}}
					/>,
					() => {return this.fragment.endTime;},
					(v) =>
					{
						this.fragment.endTime = v;
						store.dispatch(StoreActions.timeline.updateEditorUI());
					},
				)] : [],
			...this.easing ?
				[new EffectorPropertyDrawer(
					"Easing",
					PropertyTypes.DropDown,
					<DropdownProperty
						data-name={"property-easing"}
						value={this.easing}
						options={
							Object.entries(EasingFunctionTypes).filter(([key, value]) => !EasingFilter.has(value)).map(([key, value]) =>
							{
								return { display: EasingTypeToCaptionMap.get(value), value: value };
							})
						}
					/>,
					() => {return this.easing;},
					(v) => {this.setEasing(v);},
				)] : [],
			...this.isReversibleEffector() ?
				[new EffectorPropertyDrawer(
					"Reversed",
					PropertyTypes.Boolean,
					<BoolProperty
						data-name={"property-reversed"}
						value={this.reversed}
					/>,
					() => {return this.reversed;},
					(v) =>
					{
						this.reversed = v;
					},
				)] : [],
			...this.isLoopableEffector() ?
				[new EffectorPropertyDrawer(
					"Loop Count",
					PropertyTypes.Number,
					<NumberProperty
						data-name={"property-loopCount"}
						value={this.loopCount}
						min={1.0}
						step={1.0}
						defaultValue={1}
						decimals={0}
					/>,
					() => {return this.loopCount;},
					(v) => {this.setLoopCount(v);},
				)] : [],
		];
	}
}

export class EffectorPropertyDrawer
{
	name : string;
	type : PropertyTypes;
	ui : React.ReactElement;
	getValue : () => any;
	setValue : (value : any) => void;
	compareValues : (a : any, b : any) => boolean;

	constructor(name: string, type: PropertyTypes, uiElement: React.ReactElement, getterFunc: () => any, setterFunc: (value : any) => void, comparerFunc = (a: any, b: any) => {return a === b;})
	{
		this.name = name;
		this.type = type;
		this.ui = uiElement;
		this.getValue = getterFunc;
		this.setValue = setterFunc;
		this.compareValues = comparerFunc;
	}
}
